import type { PointerInputEvent, SuppressionLevel } from "../core/types.ts";
import { FrameClock, type Mechanic, type Readout } from "./mechanic.ts";

/**
 * Suppression as *time*. The game does not get worse when you stop blinking —
 * it gets faster.
 *
 * A pip sweeps back and forth across a track; tap while it is in the centre
 * zone to score. The sweep period is the only thing suppression touches, and it
 * more than triples in speed between a healthy rhythm and sustained
 * suppression. Nothing is taken away, nothing is obscured. The task is simply
 * running at a tempo you did not choose, and the only thing that slows it down
 * is blinking normally.
 *
 * This is the mechanic where the §1 inversion is hardest to game, because the
 * player has no way to convert blinking into points — only into headroom.
 *
 * Timing note: the pip's position is derived from accumulated phase, not from
 * absolute `t`. Changing the period would otherwise teleport it, because the
 * same clock reading maps to a different point in a faster cycle — so the pip
 * would jump every time suppression ticked at 10Hz.
 */

/**
 * Sweep period at a healthy rhythm, and at sustained suppression.
 *
 * The fast end is set by the scoring window it implies, not by feel. At
 * `ZONE = 0.115` the pip is inside the zone for `ZONE × period` milliseconds
 * per pass: 299ms when calm, 109ms when suppressed. The first draft used 700ms,
 * which gives a 63ms window — below visual reaction time, so the mechanic
 * stopped being hard and became impossible. Pressure has to stay playable or it
 * is not pressure, it is a wall.
 */
const SLOW_MS = 2600;
const FAST_MS = 950;

/** Half-width of the scoring zone, as a fraction of the track. */
const ZONE = 0.115;

/** Presses buffered between updates. Same reasoning as Reload's queue. */
const MAX_QUEUED = 3;

/** How long a hit or miss stays lit. */
const FLASH_MS = 140;

/** Fraction of the track the pip occupies. */
const PIP_SPAN = 6;

const clamp01 = (v: number) => (v < 0 ? 0 : v > 1 ? 1 : v);

export class Tempo implements Mechanic {
  readonly id = "tempo" as const;
  readonly hint = "tap when the pip is centred · blink to slow it down";

  #clock = new FrameClock();
  /** 0..1 through the current sweep. */
  #phase = 0;
  #suppression = 0;
  #queued = 0;
  #hits = 0;
  #misses = 0;
  #flashUntil = 0;
  #flashHit = false;
  #position = 0;

  constructor(private readonly pip: HTMLElement) {}

  onSuppression(level: SuppressionLevel): void {
    this.#suppression = level.value;
  }

  onPointer(event: PointerInputEvent): void {
    if (event.phase !== "down") return;
    // Queued, not judged. The verdict depends on where the pip is at render
    // time, and the event carries frame time from a different clock entirely.
    this.#queued = Math.min(MAX_QUEUED, this.#queued + 1);
  }

  update(t: number): void {
    const dt = this.#clock.step(t);
    const period = SLOW_MS + (FAST_MS - SLOW_MS) * this.#suppression;
    this.#phase = (this.#phase + dt / period) % 1;

    // Triangle wave: across and back, so both directions are playable.
    const position = this.#phase < 0.5 ? this.#phase * 2 : 2 - this.#phase * 2;
    this.#position = position;

    while (this.#queued > 0) {
      this.#queued--;
      if (Math.abs(position - 0.5) <= ZONE) {
        this.#hits++;
        this.#flashHit = true;
      } else {
        this.#misses++;
        this.#flashHit = false;
      }
      this.#flashUntil = t + FLASH_MS;
    }

    this.pip.style.left = `${(position * (100 - PIP_SPAN)).toFixed(2)}%`;
    const flashing = t < this.#flashUntil;
    this.pip.style.background = flashing
      ? this.#flashHit
        ? "var(--accent)"
        : "var(--danger)"
      : "";
    this.pip.style.transform = flashing ? "scale(1.45)" : "";
  }

  readout(): Readout {
    // The alert fires on tempo, not on missing. Missing is the consequence;
    // the speed is the thing you can actually do something about.
    const rushed = this.#suppression > 0.5;
    return {
      value: `${this.score}`,
      label: rushed ? "score · fast" : "score",
      ...(rushed ? { alert: true } : {}),
    };
  }

  /** Sweep period right now, in ms. The pressure, in the unit that matters. */
  get periodMs(): number {
    return SLOW_MS + (FAST_MS - SLOW_MS) * clamp01(this.#suppression);
  }

  /**
   * How long the pip is inside the zone per pass. The real difficulty number.
   *
   * Worth exposing because the period alone is misleading: a faster sweep
   * crosses the zone *more often*, so blind tapping scores at a flat rate no
   * matter the tempo. What actually gets harder is aiming, and this is the
   * quantity that measures it.
   */
  get zoneWindowMs(): number {
    return ZONE * this.periodMs;
  }

  /** Whether a tap right now would score. What the player is looking at. */
  get inZone(): boolean {
    return Math.abs(this.#position - 0.5) <= ZONE;
  }

  /**
   * Hits minus misses, and it is allowed to go negative.
   *
   * Counting hits alone made mashing the dominant strategy, and — measured —
   * mashing gets *better* as the tempo rises, because a faster sweep offers
   * more crossings per second at an unchanged ~18% blind hit rate. So the
   * mechanic inverted its own pressure: suppression punished the player who
   * aimed and left the player who mashed alone. Subtracting misses is what
   * makes the shrinking window the thing that matters.
   */
  get score(): number {
    return this.#hits - this.#misses;
  }

  get hits(): number {
    return this.#hits;
  }

  get misses(): number {
    return this.#misses;
  }

  reset(): void {
    this.#clock.reset();
    this.#phase = 0;
    this.#suppression = 0;
    this.#queued = 0;
    this.#hits = 0;
    this.#misses = 0;
    this.#flashUntil = 0;
    this.#flashHit = false;
    this.#position = 0;
    this.pip.style.left = "";
    this.pip.style.background = "";
    this.pip.style.transform = "";
  }
}

export { FAST_MS, SLOW_MS, ZONE };
